import { FC, useState } from "react";

import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

//@ts-ignore
import Icon from "@gjirafatech/gjirafa-icons/Icon";
import { LoginMethodConfirmModal } from "../../components/security/LoginMethodConfirmModal";
import {
  useFetch,
  usePreviewMode,
  useRenderPath,
  useToggle,
} from "../../hooks";
import { useLoginMethodApi } from "../../hooks/useLoginMethodApi";
import { ILoginMethodResponse, LoginMethodType } from "../../interfaces";
import { cx } from "../../utils";
import { eventLoginMethodChanged } from "../../utils/analytics";

interface ILoginMethodOption {
  value: LoginMethodType;
  title: string;
  description: string;
}

export const LoginMethod: FC = () => {
  const { t } = useTranslation();
  const { data, loading, notFound, refetch } = useLoginMethodApi();
  const { apiCall: postLoginMethod, loading: posting } = useFetch("post");
  const { visible, toggle } = useToggle();
  const [target, setTarget] = useState<LoginMethodType>("password");
  const [justUpdated, setJustUpdated] = useState(false);
  const navigate = useNavigate();
  const { isPreviewMode } = usePreviewMode();
  const { renderPath } = useRenderPath(isPreviewMode);

  const options: ILoginMethodOption[] = [
    {
      value: "magicLink",
      title: t<string>("security.loginMethodMagicLink"),
      description: t<string>("security.loginMethodMagicLinkDescription"),
    },
    {
      value: "password",
      title: t<string>("security.loginMethodPassword"),
      description: t<string>("security.loginMethodPasswordDescription"),
    },
  ];

  const onOptionClick = (value: LoginMethodType) => {
    setTarget(value);
    toggle();
  };

  const onConfirm = () => {
    if (target === "password" && !data?.hasPassword) {
      toggle();
      navigate("/change-password", { state: { from: "/login-method" } });
      return;
    }

    postLoginMethod(
      "ManageApi/LoginMethod",
      { method: target },
      () => {
        eventLoginMethodChanged(target);
        toast.success(t<string>("security.loginMethodUpdated"));
        setJustUpdated(true);
        toggle();
        refetch();
      },
      (error) => {
        toggle();
        const body = error?.response?.data;
        toast.error(
          typeof body === "string"
            ? body
            : body?.statusMessage
            ? t<string>(body.statusMessage)
            : t<string>("general.error", "Une erreur est survenue.")
        );
      }
    );
  };

  const renderContent = (loginMethod: ILoginMethodResponse) => (
    <>
      <div className="flex pb-8 mb-8 border-b border-gray-200">
        <NavLink
          to={renderPath("/security")}
          className="flex items-center justify-center flex-shrink-0 w-10 h-10 text-gray-700 hover:text-primary"
          aria-label={t<string>("general.back", "Retour")}
        >
          <span className="flex w-6 h-6">
            <Icon name="ArrowLeft" size={24} />
          </span>
        </NavLink>
        <div className="ml-6">
          <h4 className="font-medium leading-9 text-black">
            {t<string>("security.loginMethodTitle")}
          </h4>
          <p className="text-sm leading-5 mt-1 text-primary-secText">
            {t<string>("security.loginMethodDescription")}
          </p>
        </div>
      </div>

      {justUpdated && (
        <p className="text-sm text-gray-700 mt-4">
          {t<string>(
            "security.loginMethodUpdatedInfo",
            "Vos autres appareils ont été déconnectés et un e-mail vous a été envoyé."
          )}
        </p>
      )}

      <div className="mt-6">
        {options.map((option) => {
          const isCurrent = option.value === loginMethod.method;

          return (
            <button
              key={option.value}
              type="button"
              disabled={isCurrent}
              onClick={() => onOptionClick(option.value)}
              className={cx([
                "w-full text-left border rounded-lg p-4 mb-4",
                isCurrent ? "border-primary" : "hover:border-gray-400",
              ])}
            >
              <h6 className="font-medium">
                {option.title}
                {isCurrent && (
                  <span className="text-xs text-primary ml-2">
                    {t<string>("security.loginMethodCurrent")}
                  </span>
                )}
              </h6>
              <p className="text-sm text-gray-700">{option.description}</p>
            </button>
          );
        })}
      </div>

      {loginMethod.hasPassword && (
        <NavLink
          to={renderPath("/change-password")}
          className="text-primary text-sm"
        >
          {t<string>("security.changePassword")}
        </NavLink>
      )}

      {visible && (
        <LoginMethodConfirmModal
          visible={visible}
          hide={toggle}
          target={target}
          needsPassword={target === "password" && !loginMethod.hasPassword}
          onConfirm={onConfirm}
          loading={posting}
        />
      )}
    </>
  );

  return (
    <>
      <Helmet>
        <title>{t<string>("security.loginMethodTitle")}</title>
      </Helmet>

      {/* same width as MainContainer, no top padding: sub-page header sits under the app header like the template pages */}
      <section className="px-4 md:pb-6 pb-20 w-full max-w-4xl mx-auto">
        {loading ? (
          <div className="loader-container">
            <div className="loader"></div>
          </div>
        ) : notFound || !data || data.canUsePassword === false ? null : (
          renderContent(data)
        )}
      </section>
    </>
  );
};
